import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BuildingOfficeIcon, 
  UserIcon,
  CalendarIcon,
  MapPinIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ArrowLeftIcon,
  PencilIcon,
  ClockIcon,
  PhoneIcon,
  DocumentArrowDownIcon,
  UserGroupIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';

import { api } from '../../services/api';
import { toFormStaffRow } from '../../lib/staffDetails';

const OrganizationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('general');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingHakedis, setDownloadingHakedis] = useState(false);

  const { data: organizationData, isLoading, error } = useQuery({
    queryKey: ['organization', id],
    queryFn: () => api.get(`/organizations/${id}`).then(res => res.data),
    enabled: !!id
  });

  const { data: casesData, isLoading: casesLoading } = useQuery({
    queryKey: ['organization-cases', id],
    queryFn: () => api.get(`/organizations/${id}/cases`).then(res => res.data),
    enabled: !!id && activeTab === 'cases'
  });

  const organization = organizationData?.data;
  const cases = casesData?.data || [];

  const handleDownload = async (format) => {
    const setter =
      format === 'pdf' ? setDownloadingPdf
      : format === 'hakedis' ? setDownloadingHakedis
      : setDownloadingExcel;
    setter(true);
    try {
      const response = await api.get(`/organizations/${id}/proforma?format=${format}`, {
        responseType: 'blob'
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const contentType = format === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: contentType });

      // Sunucunun önerdiği dosya adını kullan (ör. "17.04.2026 ... FAALİYET RAPORU.xlsx")
      let fileName = format === 'hakedis'
        ? `hakedis-cizelgesi-${id}.${ext}`
        : `faaliyet-raporu-${id}.${ext}`;
      const disposition = response.headers?.['content-disposition'] || '';
      const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8Match) {
        try {
          fileName = decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
        } catch (e) {
          /* bozuk header — varsayılan ad kullanılır */
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: 'Hata',
        description: 'Rapor oluşturulurken hata oluştu',
        variant: 'destructive'
      });
    } finally {
      setter(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Organizasyon Bulunamadı</h2>
        <Button onClick={() => navigate('/organizations')}>Organizasyonlara Dön</Button>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const config = {
      planned: { color: 'bg-blue-100 text-blue-800', text: 'Planlanan' },
      active: { color: 'bg-green-100 text-green-800', text: 'Aktif' },
      completed: { color: 'bg-gray-100 text-gray-800', text: 'Tamamlanan' },
      cancelled: { color: 'bg-red-100 text-red-800', text: 'İptal' }
    };
    const c = config[status] || config.planned;
    return <Badge className={c.color}>{c.text}</Badge>;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : 'Belirtilmemiş';
  const formatDateTime = (d, t) => {
    if (!d) return 'Belirtilmemiş';
    return t ? `${formatDate(d)} ${t}` : formatDate(d);
  };

  const staffDetails = Array.isArray(organization.staff_details) ? organization.staff_details : [];
  const teamAssignments = organization.team_assignments || [];

  const tabs = [
    { id: 'general', label: 'Genel Bilgiler', icon: BuildingOfficeIcon },
    { id: 'cases', label: `Vakalar (${cases.length})`, icon: TableCellsIcon },
    { id: 'proforma', label: 'Faaliyet Raporu', icon: DocumentArrowDownIcon }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/organizations')} className="flex items-center space-x-2">
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Geri</span>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Organizasyon Detayı</h1>
            <p className="text-gray-600">{organization.organization_name || 'İsimsiz Organizasyon'}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(organization.status)}
          <Button size="sm" onClick={() => navigate(`/organizations/${id}/edit`)} className="flex items-center space-x-2">
            <PencilIcon className="h-4 w-4" />
            <span>Düzenle</span>
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab: Genel Bilgiler */}
      {activeTab === 'general' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <BuildingOfficeIcon className="h-5 w-5" />
                  <span>Temel Bilgiler</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Organizasyon No</label>
                    <p className="mt-1">{organization.organization_number || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Tür</label>
                    <p className="mt-1">{organization.organization_type || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Doktor Durumu</label>
                    <p className="mt-1">{organization.doctor_status || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Kurum</label>
                    <p className="mt-1">{organization.institution || '-'}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Genel Açıklama</label>
                  <p className="mt-1">{organization.general_description || '-'}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <UserIcon className="h-5 w-5" />
                  <span>Yetkili Kişi</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Yetkili Kişi</label>
                    <p className="mt-1">{organization.authorized_person || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Telefon</label>
                    <p className="mt-1 flex items-center space-x-1">
                      {organization.authorized_phone ? (
                        <><PhoneIcon className="h-4 w-4" /><span>{organization.authorized_phone}</span></>
                      ) : '-'}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Yetkili Açıklama</label>
                  <p className="mt-1">{organization.authorized_description || '-'}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <CalendarIcon className="h-5 w-5" />
                  <span>Tarih ve Saat</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Başlangıç</label>
                    <p className="mt-1 flex items-center space-x-1">
                      <ClockIcon className="h-4 w-4" />
                      <span>{formatDateTime(organization.start_date, organization.start_time)}</span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bitiş</label>
                    <p className="mt-1 flex items-center space-x-1">
                      <ClockIcon className="h-4 w-4" />
                      <span>{formatDateTime(organization.end_date, organization.end_time)}</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ek Personel */}
            {staffDetails.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <UserGroupIcon className="h-5 w-5" />
                    <span>Ek Personel</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 text-gray-500 font-medium">Ad Soyad</th>
                          <th className="text-left py-2 text-gray-500 font-medium">Görev / Unvan</th>
                          <th className="text-center py-2 text-gray-500 font-medium">Sayı</th>
                          <th className="text-center py-2 text-gray-500 font-medium">Başlangıç</th>
                          <th className="text-center py-2 text-gray-500 font-medium">Bitiş</th>
                          <th className="text-right py-2 text-gray-500 font-medium">Birim Fiyat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staffDetails.map((raw, i) => {
                          const s = toFormStaffRow(raw);
                          const price = parseFloat(s.unit_price) || 0;
                          return (
                            <tr key={i} className="border-b last:border-0">
                              <td className="py-2">{s.staff_name || '-'}</td>
                              <td className="py-2">{s.role || '-'}</td>
                              <td className="py-2 text-center">{s.count}</td>
                              <td className="py-2 text-center">{s.start_time || '-'}</td>
                              <td className="py-2 text-center">{s.end_time || '-'}</td>
                              <td className="py-2 text-right">
                                {price > 0 ? `${price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <CurrencyDollarIcon className="h-5 w-5" />
                  <span>Mali Detaylar</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Tutar</label>
                    <p className="mt-1 text-lg font-semibold">
                      {organization.amount ? `${parseFloat(organization.amount).toLocaleString('tr-TR')} ₺` : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Doktor Ücreti</label>
                    <p className="mt-1 text-lg font-semibold">
                      {organization.doctor_price ? `${parseFloat(organization.doctor_price).toLocaleString('tr-TR')} ₺` : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Ambulans Sayısı</label>
                    <p className="mt-1">{organization.ambulance_count || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Araç Numarası</label>
                    <p className="mt-1">{organization.vehicle_number || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">112 Protokol No</label>
                    <p className="mt-1">{organization.protocol_112_no || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Sözleşme</label>
                    <p className="mt-1">{organization.contract_info || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <MapPinIcon className="h-5 w-5" />
                  <span>Adres Bilgileri</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">İl</label>
                    <p className="mt-1">{organization.province || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">İlçe</label>
                    <p className="mt-1">{organization.district || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Mahalle</label>
                    <p className="mt-1">{organization.neighborhood || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Cadde/Sokak</label>
                    <p className="mt-1">{organization.street || '-'}</p>
                  </div>
                </div>
                {organization.address && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Adres</label>
                    <p className="mt-1 text-sm bg-gray-50 p-3 rounded">{organization.address}</p>
                  </div>
                )}
                {organization.address_description && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Adres Tarifi</label>
                    <p className="mt-1 text-sm bg-gray-50 p-3 rounded">{organization.address_description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Ekip Atamaları */}
            {teamAssignments.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <UserGroupIcon className="h-5 w-5" />
                    <span>Ekip Atamaları</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {teamAssignments.map((ta, i) => (
                    <div key={ta.id || i} className="border rounded-md p-3 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{ta.team?.name || `Ekip ${ta.team_id}`}</span>
                        <span className="text-xs text-gray-500">{ta.vehicle_plate || ''}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {ta.assignment_time && `Başlangıç: ${ta.assignment_time}`}
                        {ta.end_time && ` — Bitiş: ${ta.end_time}`}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Tab: Vakalar */}
      {activeTab === 'cases' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center space-x-2">
              <TableCellsIcon className="h-5 w-5" />
              <span>Organizasyon Vakaları</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {casesLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : cases.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Bu organizasyona ait vaka bulunmamaktadır.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Vaka No</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Hasta</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Yaş</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Açıklama</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Sonuç</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Ekip</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cases.map((c) => (
                      <tr key={c.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-xs">{c.case_number}</td>
                        <td className="py-3 px-4">{c.patient_name} {c.patient_surname}</td>
                        <td className="py-3 px-4">{c.patient_age || '-'}</td>
                        <td className="py-3 px-4 max-w-32 truncate">{c.initial_description || '-'}</td>
                        <td className="py-3 px-4 max-w-32 truncate">{c.result_description || '-'}</td>
                        <td className="py-3 px-4">{c.team?.name || '-'}</td>
                        <td className="py-3 px-4">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/cases/${c.id}`)}>
                            Detay
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Faaliyet Raporu */}
      {activeTab === 'proforma' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center space-x-2">
              <DocumentArrowDownIcon className="h-5 w-5" />
              <span>Faaliyet Raporu Oluştur</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm text-gray-600">
              Organizasyona ait faaliyet raporunu PDF veya Excel formatında indirin. 
              Rapor; ekip bilgileri, hasta listesi ve ücret bilgilerini içerir.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="text-xs font-medium text-gray-500">Organizasyon</label>
                <p className="font-semibold">{organization.organization_name}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Tarih</label>
                <p className="font-semibold">{formatDateTime(organization.start_date, organization.start_time)}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Ambulans Sayısı</label>
                <p className="font-semibold">{organization.ambulance_count || 0}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Doktor Ücreti</label>
                <p className="font-semibold">
                  {organization.doctor_price ? `${parseFloat(organization.doctor_price).toLocaleString('tr-TR')} ₺` : '-'}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Ek Personel Kaydı</label>
                <p className="font-semibold">{staffDetails.length} satır</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Toplam Vaka</label>
                <p className="font-semibold">{cases.length > 0 ? cases.length : 'Yüklenmedi'}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => handleDownload('pdf')}
                disabled={downloadingPdf}
                className="flex items-center space-x-2"
              >
                <DocumentArrowDownIcon className="h-5 w-5" />
                <span>{downloadingPdf ? 'PDF Oluşturuluyor...' : 'PDF İndir'}</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleDownload('excel')}
                disabled={downloadingExcel}
                className="flex items-center space-x-2"
              >
                <DocumentArrowDownIcon className="h-5 w-5" />
                <span>{downloadingExcel ? 'Excel Oluşturuluyor...' : 'Faaliyet Raporu (Excel)'}</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleDownload('hakedis')}
                disabled={downloadingHakedis}
                className="flex items-center space-x-2"
              >
                <TableCellsIcon className="h-5 w-5" />
                <span>{downloadingHakedis ? 'Hakediş Oluşturuluyor...' : 'Hakediş Çizelgesi (Excel)'}</span>
              </Button>
            </div>

            <p className="text-xs text-gray-400">
              Not: Hasta listesi olmadan da rapor oluşturulabilir. Vakalar organizasyona vaka kaydederek eklenir.
              Hakediş çizelgesindeki tutarlar Excel formülü olarak yazılır; birim fiyatı değiştirdiğinizde toplam kendiliğinden güncellenir.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OrganizationDetailPage;
